import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wind, Zap, Droplets, Flame, Leaf, LineChart, ArrowUpRight } from "lucide-react";
import { services } from "../../lib/content";

const iconMap = { Wind, Zap, Droplets, Flame, Leaf, LineChart };

export default function ServicesSection() {
    return (
        <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="services-section">
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-cyan-400 mb-6">
                            <span className="w-8 h-px bg-cyan-400/60" /> Services
                        </div>
                        <h2 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter">
                            Full-stack MEP, designed as one system.
                        </h2>
                    </div>
                    <Link
                        to="/services"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors group"
                        data-testid="services-view-all"
                    >
                        View all services
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {services.map((s, i) => {
                        const Icon = iconMap[s.icon];
                        return (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="group relative p-8 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-white/5 hover:border-blue-500/40 transition-all duration-500 overflow-hidden"
                                data-testid={`service-card-${s.title.toLowerCase()}`}
                            >
                                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-blue-500/5 blur-2xl group-hover:bg-blue-500/20 transition-colors duration-500" />

                                <div className="flex items-start justify-between mb-6">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-cyan-400" />
                                    </div>
                                    <span className="font-mono text-[10px] tracking-widest text-slate-600">0{i + 1}</span>
                                </div>

                                <h3 className="font-heading font-bold text-white text-2xl mb-2">{s.title}</h3>
                                <div className="text-xs font-mono tracking-widest uppercase text-cyan-400/80 mb-4">{s.tag}</div>
                                <p className="text-sm text-slate-400 leading-relaxed mb-6">{s.desc}</p>

                                <ul className="space-y-2">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-xs text-slate-500">
                                            <span className="w-1 h-1 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
